import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { consentCopy, consentStep2Valid, normalizeTypedDate, visitPhrase } from "../../utils/visitGuard";
import { signMismatchCopy } from "../../notereview/noteVisit";
import { combineChoiceCopy, signedNoteCopy } from "../../utils/otherUserNote";

/* Two-step consent before a recording starts on anything but today's visit
   (owner rule 2026-09-29: "never allow recording to start on an older
   appointment without explicit consent in duplicate").

   Mounted once in App.js. Callers use the promise API below, so every
   recording entry point (RecordingContext.startRecording, Editscreen) asks the
   same way:
     requestVisitConsent(info) -> true only after BOTH steps (step 1 names the
       visit; step 2 needs the visit date typed or the confirmation ticked).
     showVisitBlocked(info, todayInfo) -> explains why recording did not start
       (the patient has a visit today); resolves false.
     requestSignMismatchConsent(details) -> true only after BOTH steps, before
       signing a note filed to another day's visit (BlinkScribe sign-off 409
       VISIT_MISMATCH; the caller then sends confirm_visit_mismatch=true).
     requestCombineChoice(other) -> "add" | "separate" | null (cancelled), when
       today's visit already has ANOTHER user's note (BlinkScribe no longer
       merges into it silently, 10-06). With no host it resolves "separate":
       BlinkScribe's own default, so recording is never blocked by this.
     requestSignedNoteChoice({ signed, choices }) -> { action: "switch", visit }
       | { action: "separate" } | null (cancelled), when today's visit already
       has ANOTHER user's SIGNED note (10-06, patient 4662): a recording here
       would post into that user's encounter. choices (otherUserNote.visitChoices)
       lists the user's own visits today to record on instead; "separate" is
       offered only when there is none, with a warning. Cancel has the focus.
       With no host it resolves { action: "separate" } (BlinkScribe's default).
   With no host mounted the others resolve false: fail closed, nothing records or signs. */

let hostListener = null;

// subject: "chart" (a new recording from the chart) or "note" (the note editor).
export const requestVisitConsent = (info, { subject = "chart" } = {}) =>
  new Promise((resolve) => {
    if (!hostListener) {
      console.error("[visit-consent] no consent host mounted; recording not started");
      resolve(false);
      return;
    }
    hostListener({ mode: "consent", info, subject, resolve });
  });

export const showVisitBlocked = (info, todayInfo, { subject = "chart" } = {}) =>
  new Promise((resolve) => {
    if (!hostListener) {
      resolve(false);
      return;
    }
    hostListener({ mode: "blocked", info, todayInfo, subject, resolve: () => resolve(false) });
  });

// details: { recDay, apptDay, dayVisits } (noteVisit.mismatchDetailsFrom*).
export const requestSignMismatchConsent = (details) =>
  new Promise((resolve) => {
    if (!hostListener) {
      console.error("[visit-consent] no consent host mounted; note not signed");
      resolve(false);
      return;
    }
    hostListener({ mode: "sign-mismatch", details, resolve });
  });

// other: BlinkScribe's other_user_note ({ rowid, audiolink, user_name, ts }).
export const requestCombineChoice = (other) =>
  new Promise((resolve) => {
    if (!hostListener) {
      resolve("separate");
      return;
    }
    hostListener({ mode: "combine-choice", other, resolve, cancelValue: null });
  });

// signed: BlinkScribe's other_user_signed_note; choices: visitChoices(target).
export const requestSignedNoteChoice = ({ signed, choices } = {}) =>
  new Promise((resolve) => {
    if (!hostListener) {
      resolve({ action: "separate" });
      return;
    }
    hostListener({ mode: "signed-note", signed, choices: choices || { kind: "none", visits: [] }, resolve, cancelValue: null });
  });

const VisitConsentHost = () => {
  const [req, setReq] = useState(null); // { mode, info, todayInfo, resolve }
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState("");
  const [ticked, setTicked] = useState(false);
  const reqRef = useRef(null);

  const finish = useCallback((result) => {
    const r = reqRef.current;
    reqRef.current = null;
    setReq(null);
    setStep(1);
    setTyped("");
    setTicked(false);
    if (r) r.resolve(result);
  }, []);

  useEffect(() => {
    hostListener = (next) => {
      // One question at a time: a second start while a dialog is open is
      // refused rather than queued behind it.
      if (reqRef.current) {
        next.resolve("cancelValue" in next ? next.cancelValue : false);
        return;
      }
      reqRef.current = next;
      setStep(1);
      setTyped("");
      setTicked(false);
      setReq(next);
    };
    return () => {
      hostListener = null;
      const r = reqRef.current;
      reqRef.current = null;
      if (r) r.resolve(false);
    };
  }, []);

  if (!req) return null;

  const cancel = () => finish("cancelValue" in req ? req.cancelValue : false);

  if (req.mode === "signed-note") {
    const sc = signedNoteCopy(req.signed, req.choices);
    const visits = Array.isArray(req.choices?.visits) ? req.choices.visits : [];
    const ownVisits = req.choices?.kind === "own";
    return (
      <Dialog open onClose={cancel} maxWidth="xs" fullWidth aria-labelledby="signed-note-title">
        <DialogTitle id="signed-note-title" sx={{ fontWeight: 700, color: "#B45309" }}>
          {sc.title}
        </DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-800 leading-relaxed">{sc.body}</p>
          <p className="text-sm text-gray-800 leading-relaxed mt-3">{sc.question}</p>
          {visits.length ? (
            <div className="flex flex-col gap-2 mt-3" role="group" aria-label="Visits today">
              {visits.map((v) => (
                <Button
                  key={v.appointmentid}
                  variant={ownVisits ? "contained" : "outlined"}
                  onClick={() => finish({ action: "switch", visit: v })}
                  sx={ownVisits ? { bgcolor: "#1D2951", textTransform: "none" } : { textTransform: "none" }}
                >
                  {sc.visitLabel(v)}
                </Button>
              ))}
            </div>
          ) : null}
          {sc.allowSeparate ? (
            <p className="text-sm text-amber-800 leading-relaxed mt-3">{sc.separateWarning}</p>
          ) : null}
        </DialogContent>
        <DialogActions>
          {/* Cancel has the focus: Enter never starts a note that would post
              into another provider's signed encounter. */}
          <Button onClick={cancel} autoFocus>
            Cancel
          </Button>
          {sc.allowSeparate ? (
            <Button variant="outlined" color="warning" onClick={() => finish({ action: "separate" })}>
              {sc.separateLabel}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    );
  }

  if (req.mode === "combine-choice") {
    const cc = combineChoiceCopy(req.other);
    return (
      <Dialog open onClose={cancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1D2951" }}>{cc.title}</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-800 leading-relaxed">{cc.body}</p>
          {cc.about ? <p className="text-sm text-gray-800 leading-relaxed mt-2">{cc.about}</p> : null}
          <p className="text-sm text-gray-600 leading-relaxed mt-3">{cc.question}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancel}>Cancel</Button>
          <Button variant="outlined" onClick={() => finish("separate")}>
            {cc.separateLabel}
          </Button>
          {/* No default focus on "Add": Enter must never merge into someone
              else's note (10-06: that note was filed on the wrong chart). */}
          <Button variant="contained" onClick={() => finish("add")} sx={{ bgcolor: "#1D2951" }}>
            {cc.addLabel}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (req.mode === "blocked") {
    const phrase = visitPhrase(req.info);
    const today = req.todayInfo;
    const todayText = [today?.time, today?.provider].filter(Boolean).join(" · ");
    return (
      <Dialog open onClose={cancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1D2951" }}>Recording not started</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-800 leading-relaxed">
            This patient has a visit today{todayText ? ` (${todayText})` : ""}, but this{" "}
            {req.subject === "note" ? "note is filed to" : "chart is on"} {phrase || "a different visit"}.
          </p>
          <p className="text-sm text-gray-800 leading-relaxed mt-3">
            {req.subject === "note"
              ? "Record on today's visit from the patient's chart instead."
              : "Close the chart and open it again from today's dashboard to record on today's visit."}
          </p>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={cancel} autoFocus sx={{ bgcolor: "#1D2951" }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (req.mode === "sign-mismatch") {
    const sc = signMismatchCopy(req.details);
    const signOk = ticked || (Boolean(sc.typeTarget) && normalizeTypedDate(typed) === sc.typeTarget);
    if (step === 1) {
      return (
        <Dialog open onClose={cancel} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: "#B45309" }}>{sc.title}</DialogTitle>
          <DialogContent>
            <p className="text-sm text-gray-800 leading-relaxed">{sc.step1}</p>
          </DialogContent>
          <DialogActions>
            {/* Cancel has focus: Enter never signs into another day's visit. */}
            <Button onClick={cancel} autoFocus>
              Cancel
            </Button>
            <Button variant="outlined" color="warning" onClick={() => setStep(2)}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
    return (
      <Dialog open onClose={cancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#B45309" }}>Confirm the visit</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-800 leading-relaxed mb-3">
            {sc.typeTarget
              ? `Type the visit date (${sc.typeTarget}) or tick the box to sign the note into that visit.`
              : "Tick the box to sign the note into that visit."}
          </p>
          {sc.typeTarget ? (
            <TextField
              size="small"
              fullWidth
              label={`Visit date (${sc.typeTarget})`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              inputProps={{ autoComplete: "off", "aria-label": "Visit date" }}
              sx={{ mb: 1 }}
            />
          ) : null}
          <FormControlLabel
            control={<Checkbox checked={ticked} onChange={(e) => setTicked(e.target.checked)} />}
            label={<span className="text-sm">{sc.tickLabel}</span>}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cancel} autoFocus>
            Cancel
          </Button>
          <Button variant="contained" color="warning" disabled={!signOk} onClick={() => finish(true)}>
            {sc.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const copy = consentCopy(req.info, req.subject);
  const step2Ok = consentStep2Valid({ typed, ticked }, req.info);

  if (step === 1) {
    return (
      <Dialog open onClose={cancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#B45309" }}>{copy.title}</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-800 leading-relaxed">{copy.step1}</p>
        </DialogContent>
        <DialogActions>
          {/* Cancel has focus: Enter never walks into a past-visit recording. */}
          <Button onClick={cancel} autoFocus>
            Cancel
          </Button>
          <Button variant="outlined" color="warning" onClick={() => setStep(2)}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={cancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: "#B45309" }}>Confirm the visit</DialogTitle>
      <DialogContent>
        <p className="text-sm text-gray-800 leading-relaxed mb-3">
          {copy.typeTarget
            ? `Type the visit date (${copy.typeTarget}) or tick the box to start recording.`
            : "Tick the box to start recording."}
        </p>
        {copy.typeTarget ? (
          <TextField
            size="small"
            fullWidth
            label={`Visit date (${copy.typeTarget})`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            inputProps={{ autoComplete: "off", "aria-label": "Visit date" }}
            sx={{ mb: 1 }}
          />
        ) : null}
        <FormControlLabel
          control={<Checkbox checked={ticked} onChange={(e) => setTicked(e.target.checked)} />}
          label={<span className="text-sm">{copy.tickLabel}</span>}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} autoFocus>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!step2Ok}
          onClick={() => finish(true)}
        >
          Start recording
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitConsentHost;
